import React, { useEffect, useRef, useState, useMemo } from "react";
import { Card } from "primereact/card";
import MyButton from "../components/UI/Button";
import PresortDemo from "../components/table/DataTable";
import { useActionsHook } from "../hooks/useActionsHook";
import { boxColumn, createOrderToBox } from "../metadata/box";
import { Accordion, AccordionTab } from "primereact/accordion";
import Editor from "../components/UI/editor";
import { Toast } from "primereact/toast";
import JsBarcode from "jsbarcode";
import {
  useCreateOrUpdateBoxMutation,
  useGetBoxQuery,
  useSetOrderToBoxMutation,
  useDelOrderToBoxMutation
} from "../store/api/boxApi";
import { useGetOrderByTCodeQuery, useGetOrderSearchQuery } from "../store/api/orderAPI";
import { useParams } from "react-router-dom";
import { ColumnGroup } from "primereact/columngroup";
import { Row } from "primereact/row";
import { Column } from "primereact/column";
import { useTranslation } from "react-i18next";
import { useTranslatedColumns } from "../hooks/useTranslatedColumns";

export default function BoxDetail() {
  const { t } = useTranslation();
  const { boxId } = useParams();

  const [formData, setFormData] = useState({ boxId, desc: "test", tcode: "" });
  const [openBox, setOpenBox] = useState(null);
  const [userInteracted, setUserInteracted] = useState(false);
  const [lastGuid, setLastGuid] = useState()
  const boxRef = useRef(null);
  const barcodeRef = useRef(null);
  const toast = useRef(null);
  const audioSuccess = useRef(new Audio("/scun.mp3"));
  const audioError = useRef(new Audio("/error.mp3"));

  const createOrderToBoxTr = useTranslatedColumns(createOrderToBox);

  const [debouncedTCode, setDebouncedTCode] = useState(formData.tcode);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedTCode(formData.tcode), 300);
    return () => clearTimeout(handler);
  }, [formData.tcode]);

  const { data: box = [] } = useGetBoxQuery({ boxId: formData.boxId ?? boxId });
  const { data: order = [], refetch: refetchOrder } = useGetOrderByTCodeQuery({ boxId: formData.boxId });
  const { data: currentOrder = [] } = useGetOrderSearchQuery({ tcode: debouncedTCode.trim() }, { skip: !debouncedTCode });

  const [setOrderToBox] = useSetOrderToBoxMutation();
  const [delOrderToBox] = useDelOrderToBoxMutation();
  const [createOrUpdateBox] = useCreateOrUpdateBoxMutation();

  useEffect(() => {
    const handleInteraction = () => setUserInteracted(true);
    window.addEventListener("keydown", handleInteraction);
    window.addEventListener("click", handleInteraction);
    return () => {
      window.removeEventListener("keydown", handleInteraction);
      window.removeEventListener("click", handleInteraction);
    };
  }, []);

  useEffect(() => {
    if (box.length) {
      const firstBox = { ...box[0], v_weight: (box[0].width * box[0].height * box[0].length) / 6000 };
      setOpenBox(firstBox);
      boxRef.current?.focus();
    }
  }, [box]);

  useEffect(() => {
    if (formData.boxId) {
      const intervalId = setInterval(() => {
        refetchOrder();
      }, 1500);

      return () => clearInterval(intervalId);
    }
  }, [formData.boxId, refetchOrder]);


  const onEditBox = (e, attr, value) => setOpenBox(state => ({ ...state, [attr]: value }));

  const showSuccess = msg => {
    if (userInteracted) audioSuccess.current?.play();
    toast.current?.show({ severity: "success", summary: "Success", detail: msg, life: 5000 });
  };
  const showError = msg => {
    if (userInteracted) audioError.current?.play();
    toast.current?.show({ severity: "error", summary: msg, detail: msg, life: 10000 });
  };

  const actions = useActionsHook([{
    onClick: async (e, data) => {
      const result = await delOrderToBox({ guid: data.guid }).unwrap();
      if (result.success) {
        showSuccess(`${t("Заказ успешно удален")}: ${data.tracking_number}`);
      } else showError(t("Не удалось удалить заказ"));
    },
    icon: "pi pi-trash"
  }]);

  const handleOrderProcessing = async scannedOrder => {
    if (!openBox?.guid) return showError(t("boxNotOpen"));
    if (scannedOrder.status === 1) return showError(t("orderAlreadyProcessed"));
    if (order.find(el => el.guid === scannedOrder.guid)) return showError(t("orderScunned"));
    try {
      const res = await setOrderToBox({ order: scannedOrder.guid, box: openBox.guid, desc: formData.desc }).unwrap();
      showSuccess(t("orderAdded"));
      setFormData(state => ({ ...state, tcode: "" }));
      setLastGuid(res[0].guid)
    } catch (err) {
      showError(t("orderAddFailed"));
    }
  };
  const handleScan = () => {
    if (!formData.tcode) return;
    const alreadyInBox = order.find(item => String(item.tracking_number).trim() === String(formData.tcode).trim());
    if (alreadyInBox) {
      showError(t("Заказ уже в коробке!"));
      setFormData(state => ({ ...state, tcode: "" }));
      return;
    }
    if (!currentOrder?.length) {
      showError(t("Заказ не найден!"));
      setFormData(state => ({ ...state, tcode: "" }));
      return;
    }
    handleOrderProcessing(currentOrder[0]);
  };
  const onBlur = (e, attr, value) => {
    setFormData(state => ({ ...state, [attr]: value.trim() }));
    handleScan();
  };
  const firstGuid = currentOrder?.[0]?.guid;
  useEffect(() => {
    if (!firstGuid) return;

    handleScan()
  }, [firstGuid]);

  const totals = useMemo(() => {
    return order.reduce(
      (acc, item) => {
        acc.width += item.width;
        acc.height += item.height;
        acc.length += item.length;
        acc.v_weight += item.v_weight;
        acc.gross_weight += item.gross_weight;
        return acc;
      },
      { width: 0, height: 0, length: 0, v_weight: 0, gross_weight: 0 }
    );
  }, [order]);

  const footerGroup = (
    <ColumnGroup>
      <Row>
        <Column footer={t("results")} colSpan={5} footerStyle={{ textAlign: "right" }} />
        <Column footer={""} />
        <Column footer={totals.gross_weight.toFixed(2)} />
        <Column footer={totals.v_weight.toFixed(2)} />
      </Row>
    </ColumnGroup>
  );

  const handlePrint = () => {
    if (!openBox?.guid) return;

    JsBarcode("#barcode", openBox.guid, {
      format: "CODE128",
      displayValue: true,
      width: 2,
      height: 60
    });

    const printContents = barcodeRef.current.innerHTML;
    const win = window.open("", "", "height=300,width=600");
    win.document.write("<html><head><title>Print</title></head><body>");
    win.document.write(printContents);
    win.document.write("</body></html>");
    win.document.close();
    win.focus();
    win.print();
    win.close();
  };

  return (
    <>
      <Toast ref={toast} />
      <Accordion activeIndex={0}>
        <AccordionTab header={t("box")}>
          <div className="user-sidebar">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 0 }}>
              <MyButton label={t("print_label")} icon="pi pi-print" onClick={handlePrint} />
            </div>
            <p>{openBox ? `${openBox.partial_name ?? ""} ${openBox.tracking_code ?? ""}` : ""}</p>
            <div ref={barcodeRef} style={{ display: "none" }}>
              <svg id="barcode"></svg>
            </div>
            <div className="user-sidebar-edit">
              {boxColumn.filter(i => i.editor).map(el => (
                <div key={el.label}>
                  <Editor {...el} onBlur={onEditBox} data={openBox} label={t(el.label)} />
                </div>
              ))}
              <MyButton
                label={t("save")}
                onClick={async () => {
                  await createOrUpdateBox(openBox);
                  showSuccess(t("successSaved"));
                }}
              />
            </div>
          </div>
        </AccordionTab>
      </Accordion>

      <Card>
        <div style={{ display: "flex", alignItems: "center", gap: 12, width: "100%" }}>
          <Editor
            data={formData}
            onBlur={onBlur}
            attr="tcode"
            refEl={boxRef}
            onKeyDown={e => e.key === "Enter" && handleScan()}
          />
          <MyButton label={t("put_box")} icon="pi pi-barcode" outlined onClick={handleScan} />
        </div>

        <PresortDemo
          isLoading={false}
          key={"guid"}
          data={order}
          column={createOrderToBoxTr}
          toggleSidebar={() => { }}
          footerColumnGroup={footerGroup}
          actions={actions}
          exportExcelBool={true}
          lastGuid={lastGuid}
        />
      </Card>
    </>
  );
}